import { lazy } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { api, isTruthy } from 'common';

import { entityTestPath } from './EntityTest/path';
import { NotFoundPage } from './NotFoundPage';
import { LazyLoader } from '../components/LazyLoader';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { FeatureFlagKey, useFlag } from '../utils/flags';
import { HumanTaskAppVariant } from './Agents/Flows/HumanTasks/types';

const ApiInstanceRoutes = lazy(() =>
  import('./ApiInstance/Routes').then((module) => ({ default: module.ApiInstanceRoutes }))
);
const PracticeConfigApiInstanceRoutes = lazy(() =>
  import('./OrchestratorInstance/PracticeConfig/Api/Routes').then((module) => ({
    default: module.PracticeConfigApiInstanceRoutes,
  }))
);
const AgenticChatLogsPage = lazy(() =>
  import('./AgenticChatLogs/Page').then((module) => ({ default: module.AgenticChatLogsPage }))
);
const AgenticChatPage = lazy(() =>
  import('./AgenticChat/Page').then((module) => ({ default: module.AgenticChatPage }))
);
const Assistants = lazy(() =>
  import('./Assistants/Routes').then((module) => ({
    default: module.AssistantRoutes,
  }))
);
const ContactStatusRoutes = lazy(() =>
  import('./ContactStatus/Routes').then((module) => ({ default: module.ContactStatusRoutes }))
);
const ConnectorRoutes = lazy(() =>
  import('./Connector/Routes').then((module) => ({ default: module.ConnectorRoutes }))
);
const DiagnosticsRoutes = lazy(() =>
  import('./Diagnostics').then((module) => ({ default: module.DiagnosticsRoutes }))
);
const DocumentTemplateRoutes = lazy(() =>
  import('./DocumentTemplates').then((module) => ({ default: module.DocumentTemplateRoutes }))
);
const EhrInstanceRoutes = lazy(() =>
  import('./EhrInstance/Routes').then((module) => ({ default: module.EhrInstanceRoutes }))
);
const EhrInstanceJobConfigRoutes = lazy(() =>
  import('./JobConfig').then((module) => ({
    default: module.EhrInstanceJobConfigRoutes,
  }))
);
const PracticeConfigEhrInstanceRoutes = lazy(() =>
  import('./OrchestratorInstance/PracticeConfig/Ehr').then((module) => ({
    default: module.PracticeConfigEhrInstanceRoutes,
  }))
);
const EncounterRoutes = lazy(() =>
  import('./Encounters').then((module) => ({ default: module.EncounterRoutes }))
);
const EntityRoutes = lazy(() =>
  import('./Entity').then((module) => ({ default: module.EntityRoutes }))
);
const EntityTest = lazy(() => import('./EntityTest'));
const FeedRoutes = lazy(() =>
  import('./Feeds/Router').then((module) => ({ default: module.FeedRoutes }))
);
const FindingRoutes = lazy(() =>
  import('./Findings').then((module) => ({ default: module.FindingRoutes }))
);
const FingerprintRoutes = lazy(() =>
  import('./Fingerprint').then((module) => ({ default: module.FingerprintRoutes }))
);
const Home = lazy(() => import('./Home'));
const Messaging = lazy(() =>
  import('./Messaging/Routes').then((module) => ({
    default: module.MessagingRoutes,
  }))
);
const MindtickleSso = lazy(() => import('./Mindtickle'));
const NetworkingRoutes = lazy(() =>
  import('./Networking/Routes').then((module) => ({ default: module.NetworkingRoutes }))
);
const NoteTemplateRoutes = lazy(() =>
  import('./NoteTemplates').then((module) => ({ default: module.NoteTemplateRoutes }))
);
const PayoutRoutes = lazy(() =>
  import('./Payments/PayoutRoutes').then((module) => ({ default: module.PayoutRoutes }))
);
const PracticeConfigRoutes = lazy(() =>
  import('./PracticeConfig/Route').then((module) => ({ default: module.PracticeConfigRoutes }))
);
const PrevisitFormTemplateRoutes = lazy(() =>
  import('./PrevisitFormTemplate').then((module) => ({
    default: module.PrevisitFormTemplateRoutes,
  }))
);
const ProviderRoutes = lazy(() =>
  import('./Providers/Routes').then((module) => ({ default: module.ProviderRoutes }))
);
const RecommendationTemplateRoutes = lazy(() =>
  import('./RecommendationTemplates').then((module) => ({
    default: module.RecommendationTemplateRoutes,
  }))
);
const PrevisitLinkRoutes = lazy(() =>
  import('./PrevisitLink').then((module) => ({ default: module.PrevisitLinkRoutes }))
);
const ReportRoutes = lazy(() =>
  import('./Reports').then((module) => ({ default: module.ReportRoutes }))
);
const UtilizationRoutes = lazy(() =>
  import('./Utilization/Routes').then((module) => ({ default: module.UtilizationRoutes }))
);
const TasksRoutes = lazy(() => import('./Tasks/TasksRoutes'));
const Version = lazy(() => import('./Version'));
const LazyAgents = lazy(() => import('./Agents/index'));
const LazyNotifications = lazy(() => import('./Notifications/index'));
const ExternalDocPacketRoutes = lazy(() =>
  import('./ExternalDocPackets').then((module) => ({ default: module.ExternalDocPacketRoutes }))
);
const ImpersonationPage = lazy(() =>
  import('./Impersonation/Impersonation').then((module) => ({ default: module.ImpersonationPage }))
);
const OrganizationRoutes = lazy(() =>
  import('./Organizations/Routes').then((module) => ({ default: module.OrganizationRoutes }))
);
const CanonicalFieldsForType = lazy(() =>
  import('./OrchestratorInstance/CanonicalFields').then((module) => ({
    default: module.CanonicalFieldsForType,
  }))
);
const DecisionTreeRoutes = lazy(() =>
  import('./DecisionTree/Routes').then((module) => ({ default: module.DecisionTreeRoutes }))
);
const Triggers = lazy(() =>
  import('./Triggers/Routes').then((module) => ({ default: module.TriggersRoutes }))
);
const PaymentPage = lazy(() =>
  import('./Payments/ReportV2/Page').then((module) => ({ default: module.PaymentPage }))
);
const StripeAccountRoutes = lazy(() =>
  import('./StripeAccounts/Routes').then((module) => ({ default: module.StripeAccountRoutes }))
);
const NoteRoutes = lazy(() => import('./Notes/index'));
const Practices = lazy(() => import('./Practices'));
const PracticeGroups = lazy(() =>
  import('./PracticeGroups/Routes').then((module) => ({ default: module.PracticeGroupRoutes }))
);
const ServiceAccounts = lazy(() => import('./ServiceAccounts/index'));
const PermissionGroupsRoutes = lazy(() =>
  import('./PermissionGroups/Routes').then((module) => ({ default: module.PermissionGroupRoutes }))
);

const EhrSystemRoutes = lazy(() =>
  import('./EhrSystem/Routes').then((module) => ({ default: module.EhrSystemsRoutes }))
);

const KnowledgeBase = lazy(() =>
  import('./KnowledgeBase/Routes').then((module) => ({ default: module.KnowledgeBaseRoutes }))
);

const Payors = lazy(() => import('./Payors/index'));
const UserRoutes = lazy(() =>
  import('./Users/Routes').then((module) => ({ default: module.UserRoutes }))
);
const FrontDeskDevicesRoutes = lazy(() =>
  import('./FrontDeskDevices/Routes').then((module) => ({ default: module.FrontDeskDevicesRoutes }))
);
const FlowWorkRoutes = lazy(() =>
  import('./Agents/Flows/Work/Routes').then((module) => ({ default: module.FlowWorkRoutes }))
);
const HumanTaskRoutes = lazy(() =>
  import('./Agents/Flows/HumanTasks/Routes').then((module) => ({ default: module.HumanTaskRoutes }))
);
const AssignmentGroupRoutes = lazy(() =>
  import('./Agents/Flows/AssignmentGroups/Routes').then((module) => ({
    default: module.AssignmentGroupRoutes,
  }))
);
const FlowActivityRoutes = lazy(() =>
  import('./FlowActivity/Routes').then((module) => ({ default: module.FlowActivityRoutes }))
);
const PhoneNumberConfiguration = lazy(() =>
  import('./PhoneNumberConfiguration/Routes').then((module) => ({
    default: module.PhoneNumberConfigurationRoutes,
  }))
);
const VoiceAssistants = lazy(() =>
  import('./VoiceAssistants/Routes').then((module) => ({
    default: module.VoiceAssistantsRoutes,
  }))
);

export function AppRoutes() {
  const { pathname, search } = useLocation();

  const connectorsOptions = useFlag({
    flagKey: FeatureFlagKey.connectorsOptions,
  });

  const networkingOptions = useFlag({
    flagKey: FeatureFlagKey.networkingOptions,
  });

  const agenticPlatformOptions = useFlag({
    flagKey: FeatureFlagKey.agenticPlatformOptions,
  });

  return (
    <LazyLoader>
      <Switch>
        {/**
         * When clicking a link that goes up a level, such as `to="..", the browser will append a trailing slash
         * This breaks code like `${match.path}/flows/definitions/:id` because will end up with a double slash.
         * This solution works well.
         * The Redirect replaces the url with the same url but without the trailing slash. It also preserves the search params.
         * https://jasonwatmore.com/post/2020/03/26/react-router-relative-links-example
         */}
        <Redirect
          from="/:url*(/+)"
          to={{
            pathname: pathname.slice(0, -1),
            search,
          }}
        />
        <Route path="/api-instances" component={ApiInstanceRoutes} />
        <ProtectedRoute
          path="/appointment-kinds"
          permissionRequirement={{
            and: [
              api.Group.OrchestratorPermission.ViewCanonicalFieldTypes,
              api.Group.OrchestratorPermission.ViewCanonicalFields,
              api.Group.OrchestratorPermission.ViewAppointmentKinds,
            ],
          }}
        >
          <CanonicalFieldsForType canonicalFieldTypeName="AppointmentKind" />
        </ProtectedRoute>
        <Route path="/contact-status" component={ContactStatusRoutes} />
        {connectorsOptions.isListScreenEnabled && (
          <Route path="/connections" component={ConnectorRoutes} />
        )}
        {/* Connectors was renamed to Connections; redirect old links until callers migrate. */}
        {connectorsOptions.isListScreenEnabled && (
          <Route
            path="/connectors"
            render={({ location }) => (
              <Redirect
                to={{
                  pathname: location.pathname.replace(/^\/connectors(?=\/|$)/, '/connections'),
                  search: location.search,
                  hash: location.hash,
                }}
              />
            )}
          />
        )}
        <Route path="/decision-trees" component={DecisionTreeRoutes} />
        <Route path="/recommendation-templates" component={RecommendationTemplateRoutes} />
        <Route path="/previsit-links" component={PrevisitLinkRoutes} />
        <Route path="/diagnostics" component={DiagnosticsRoutes} />
        <Route path="/document-templates" component={DocumentTemplateRoutes} />
        <Route path="/ehr-instances" component={EhrInstanceRoutes} />
        <Route path="/encounters" component={EncounterRoutes} />
        <Route path="/entity" component={EntityRoutes} />
        <Route path={entityTestPath()} component={EntityTest} />
        <Route path="/external-doc-packets" component={ExternalDocPacketRoutes} />
        <Route path="/feeds" component={FeedRoutes} />
        <Route path="/findings" component={FindingRoutes} />
        <Route path="/triggers" component={Triggers} />
        {networkingOptions.isEnabled && <Route path="/networking" component={NetworkingRoutes} />}
        <Route path="/impersonation" component={ImpersonationPage} />
        <Route path="/job-configs" component={EhrInstanceJobConfigRoutes} />
        <Route path="/note-templates" component={NoteTemplateRoutes} />
        <Route path="/notifications" component={LazyNotifications} />
        <Route path="/fingerprint" component={FingerprintRoutes} />
        <Route path="/flow-activity" component={FlowActivityRoutes} />
        <Route path="/messaging" component={Messaging} />
        <Route path="/assistant" component={Assistants} />
        {isTruthy(agenticPlatformOptions.isEnabled) && (
          <ProtectedRoute
            path="/agentic-chat-logs/:sessionId?"
            component={AgenticChatLogsPage}
            permissionRequirement={api.Group.Permission.ViewAllChatSessions}
            roleRequirement={api.User.Role.Admin}
          />
        )}
        {isTruthy(agenticPlatformOptions.isEnabled) && (
          <Route path="/chat/:sessionId?" component={AgenticChatPage} />
        )}
        <Route path="/assistants" component={VoiceAssistants} />
        <Route path="/payments/report" component={PaymentPage} />
        <Route path="/payouts" component={PayoutRoutes} />
        <ProtectedRoute
          path="/phone-numbers"
          component={PhoneNumberConfiguration}
          permissionRequirement={api.Group.Permission.ViewAssistantPhoneConfiguration}
        />
        <Route path="/organizations" component={OrganizationRoutes} />
        <Route path="/practice-configs" component={PracticeConfigRoutes} />
        <Route path="/practice-config-ehr-instances" component={PracticeConfigEhrInstanceRoutes} />
        <Route path="/practice-config-api-instances" component={PracticeConfigApiInstanceRoutes} />
        <Route path="/previsit-form-templates" component={PrevisitFormTemplateRoutes} />
        <Route path="/reports" component={ReportRoutes} />
        <Route path="/utilization" component={UtilizationRoutes} />
        <Route path="/tasks" component={TasksRoutes} />
        <Route path="/providers" component={ProviderRoutes} />
        <Route path="/version" component={Version} />
        <Route path="/mindtickle-sso" component={MindtickleSso} />
        <Route path="/agents" component={LazyAgents} />
        <Route path="/stripe-accounts" component={StripeAccountRoutes} />
        <Route path="/users" component={UserRoutes} />
        <Route path="/notes" component={NoteRoutes} />
        <Route path="/practices" component={Practices} />
        <Route path="/practice-groups" component={PracticeGroups} />
        <Route path="/permission-groups" component={PermissionGroupsRoutes} />
        <Route path="/payors" component={Payors} />
        <Route path="/ehr-systems" component={EhrSystemRoutes} />
        <Route path="/front-desk-devices" component={FrontDeskDevicesRoutes} />
        <Route exact path="/" component={Home} />
        <Route path="/service-accounts" component={ServiceAccounts} />
        <Route path="/knowledge-base" component={KnowledgeBase} />
        <Route path="/front-desk-devices" component={FrontDeskDevicesRoutes} />
        <Route path="/signals" component={FlowWorkRoutes} />
        <Route path="/human-tasks">
          <HumanTaskRoutes appVariant={HumanTaskAppVariant.StaffApp} />
        </Route>
        <Route
          path="/human-task-categories"
          render={({ location }) => (
            <Redirect
              to={{
                pathname: location.pathname.replace(
                  /^\/human-task-categories(?=\/|$)/,
                  '/assignment-groups'
                ),
                search: location.search,
              }}
            />
          )}
        />
        <Route path="/assignment-groups" component={AssignmentGroupRoutes} />
        <Route path="*" component={NotFoundPage} />
      </Switch>
    </LazyLoader>
  );
}
